import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FunnelHeader, FunnelProgress } from '@/components/health-check/FunnelChrome';
import { loadIntake } from '@/lib/bhc';
import '@/styles/business-health-check.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/health-check`;

export default function Checkout() {
  const navigate = useNavigate();
  const intake = useMemo(() => loadIntake(), []);
  const isTrust = intake.variant === 'trust';
  const product = isTrust ? 'Trust & Estate Health Check' : 'Mid-Year Business Health Check';
  const [processing, setProcessing] = useState(false);
  const [err, setErr] = useState('');

  // Guard: need a signed-terms token to be here.
  useEffect(() => {
    if (!intake.engagement_token) {
      navigate('/business-health-check/terms', { replace: true });
    }
  }, []); // eslint-disable-line

  const pay = async () => {
    if (processing) return;
    setErr('');
    setProcessing(true);
    try {
      const res = await fetch(`${API}/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ engagement_token: intake.engagement_token, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error(`checkout ${res.status}`);
      const data = await res.json();
      if (!data.url) throw new Error('no checkout url');
      window.location.href = data.url; // redirect to Stripe hosted checkout
    } catch {
      setErr('We couldn\u2019t start secure checkout. Please go back, re-sign the terms, and try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="bhc-root">
      <Helmet><title>Secure payment · Health Check | KGOB</title>
        <meta name="description" content="Secure payment for your KGOB Business Health Check engagement." /><meta name="robots" content="noindex" /></Helmet>
      <FunnelHeader back={{ label: 'Back to terms', to: '/business-health-check/terms' }} secure="256-bit encrypted" />
      <FunnelProgress step={3} />

      <main className="funnel">
        <div className="wrap funnel__grid">
          <div>
            <div className="funnel__head">
              <span className="eyebrow">Step 3 · Secure payment</span>
              <h1>Complete your engagement.</h1>
              <p>One fixed fee. The moment your payment is confirmed, you're a KGOB client and a member of our team is assigned to your engagement.</p>
            </div>

            <div className="panel">
              <p className="panel__title">Payment</p>
              <span className="paybrand" style={{ marginTop: '6px' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                Payments securely processed by Stripe · we never see or store your card
              </span>
              <div className="callout callout--info" style={{ marginTop: '22px' }}>
                You'll be taken to Stripe's secure, encrypted checkout to enter your card. After payment you're returned here and instantly become a KGOB client.
              </div>
              <div className="callout" style={{ marginTop: '14px' }}>
                <strong>Non-refundable once work commences.</strong> By paying, you confirm the engagement terms you signed (v1.0) and authorize KGOB to begin work promptly.
              </div>
              {err && <div className="callout callout--warn" style={{ marginTop: '14px' }} data-testid="checkout-error">{err}</div>}
              <div className="actionbar">
                <button type="button" className="btn btn--primary btn--lg btn--block" onClick={pay} disabled={processing} data-testid="pay-btn">
                  {processing ? 'Starting secure checkout…' : 'Pay $4,995 securely'}
                </button>
              </div>
              <p className="actionbar__hint center" style={{ width: '100%', textAlign: 'center', marginTop: '14px' }}>Statement descriptor: <strong>KGOB HEALTH CHECK</strong></p>
            </div>
          </div>

          <aside className="summary">
            <div className="summary__card">
              <h3>Order summary</h3>
              <div className="lineitem" style={{ marginTop: '14px' }}>
                <div className="lineitem__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg></div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>{product}</div>
                  <div style={{ color: 'rgba(255,255,255,.6)', fontSize: '13px', marginTop: '4px' }}>Fixed-fee CPA diagnostic · Jan–Jun</div>
                </div>
              </div>
              <div className="summary__line" style={{ border: 0 }}><span>Subtotal</span><span>$4,995.00</span></div>
              <div className="summary__total"><span style={{ fontSize: '13px', color: 'var(--gold-300)', textTransform: 'uppercase', letterSpacing: '.12em' }}>Total due today</span><b>$4,995</b></div>
              <ul className="summary__incl">
                <li><span className="tick">✓</span> Bookkeeping catch-up + financial statements</li>
                <li><span className="tick">✓</span> Tax projection &amp; savings opportunity review</li>
                <li><span className="tick">✓</span> Payroll &amp; sales-tax compliance reviews</li>
                <li><span className="tick">✓</span> IRS &amp; state account review (with Form 2848)</li>
                <li><span className="tick">✓</span> Written findings report + 60-min strategy</li>
              </ul>
              <p className="summary__note">Non-refundable once work begins. Tax figures are estimates based on information provided, not guarantees.</p>
            </div>
          </aside>
        </div>
      </main>

      {processing && (
        <div className="processing-overlay" data-testid="processing">
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
            <p style={{ color: '#fff', marginTop: '18px', fontFamily: 'var(--font-display)', fontSize: '20px' }}>Taking you to secure checkout…</p>
          </div>
        </div>
      )}
    </div>
  );
}
